import React, { createContext, useContext, useState, useMemo, useEffect, useCallback } from 'react'
import {
  ThemeProvider as MuiThemeProvider,
  createTheme,
  CssBaseline,
  PaletteMode,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { getLanguageDir, isRTL, LanguageCode, LANGUAGES } from '../i18n'
import { prefixer } from 'stylis'
import rtlPlugin from 'stylis-plugin-rtl'
import createCache from '@emotion/cache'
import { CacheProvider } from '@emotion/react'

interface ThemeContextValue {
  mode: PaletteMode
  toggleMode: () => void
  language: LanguageCode
  setLanguage: (lang: LanguageCode) => void
}

const ThemeContext = createContext<ThemeContextValue>({
  mode: 'light',
  toggleMode: () => {},
  language: 'en',
  setLanguage: () => {},
})

export const useThemeMode = () => useContext(ThemeContext)

// Emotion caches for LTR and RTL
const ltrCache = createCache({ key: 'mui' })
const rtlCache = createCache({ key: 'muirtl', stylisPlugins: [prefixer, rtlPlugin] })

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const { i18n } = useTranslation()

  const [mode, setMode] = useState<PaletteMode>(() => {
    return (localStorage.getItem('themeMode') as PaletteMode) ?? 'light'
  })

  const [language, setLanguageState] = useState<LanguageCode>(() => {
    return (localStorage.getItem('omnitrace-lang') as LanguageCode) ?? 'en'
  })

  const toggleMode = () => {
    setMode((prev) => {
      const next = prev === 'light' ? 'dark' : 'light'
      localStorage.setItem('themeMode', next)
      return next
    })
  }

  const setLanguage = useCallback((lang: LanguageCode) => {
    setLanguageState(lang)
    localStorage.setItem('omnitrace-lang', lang)
    i18n.changeLanguage(lang)
  }, [i18n])

  // Apply RTL direction to the document
  const dir = getLanguageDir(language)
  useEffect(() => {
    document.documentElement.dir = dir
    document.documentElement.lang = language
  }, [dir, language])

  const theme = useMemo(
    () => {
      const isDark = mode === 'dark'
      return createTheme({
        direction: dir,
        palette: {
          mode,
          primary: {
            main: '#1597E5',
            light: '#62C6F5',
            dark: '#0876BA',
            contrastText: '#ffffff',
          },
          secondary: { main: '#20C997', light: '#7CE8C8', dark: '#07966E' },
          success: { main: '#23B26D' },
          warning: { main: '#F6A723' },
          error: { main: '#EF5261' },
          info: { main: '#4B7BEC' },
          background: {
            default: isDark ? '#07111F' : '#F3F7FB',
            paper: isDark ? '#0D1A2B' : '#FFFFFF',
          },
          text: {
            primary: isDark ? '#F5F9FF' : '#10243E',
            secondary: isDark ? '#91A4BD' : '#60758E',
          },
          divider: isDark ? 'rgba(163, 190, 218, 0.14)' : 'rgba(29, 65, 101, 0.10)',
        },
        typography: {
          fontFamily: isRTL(language)
            ? '"Noto Sans Arabic", "Noto Sans Kurdish", "Segoe UI", "Tahoma", sans-serif'
            : '"Inter", "Avenir Next", "Segoe UI", sans-serif',
          h1: { fontWeight: 800, letterSpacing: '-0.045em' },
          h2: { fontWeight: 800, letterSpacing: '-0.04em' },
          h3: { fontWeight: 800, letterSpacing: '-0.035em' },
          h4: { fontWeight: 800, letterSpacing: '-0.03em' },
          h5: { fontWeight: 750, letterSpacing: '-0.025em' },
          h6: { fontWeight: 700, letterSpacing: '-0.015em' },
          button: { fontWeight: 700, letterSpacing: '-0.01em' },
          body1: { lineHeight: 1.6 },
          body2: { lineHeight: 1.55 },
        },
        shape: { borderRadius: 16 },
        components: {
          MuiCssBaseline: {
            styleOverrides: {
              body: {
                backgroundImage: isDark
                  ? 'radial-gradient(circle at 82% -10%, rgba(21,151,229,.14), transparent 34%)'
                  : 'radial-gradient(circle at 82% -10%, rgba(21,151,229,.11), transparent 32%)',
                backgroundAttachment: 'fixed',
                minWidth: 320,
                WebkitFontSmoothing: 'antialiased',
                textRendering: 'optimizeLegibility',
              },
              html: { scrollBehavior: 'smooth' },
              '#root': { minHeight: '100vh' },
              '*': { scrollbarWidth: 'thin', scrollbarColor: `${isDark ? '#29405B' : '#C8D6E5'} transparent` },
              '::selection': { backgroundColor: 'rgba(21,151,229,.22)' },
              ':focus-visible': {
                outline: '3px solid rgba(21,151,229,.34)',
                outlineOffset: 2,
              },
              '@media (prefers-reduced-motion: reduce)': {
                'html': { scrollBehavior: 'auto' },
                '*, *::before, *::after': {
                  animationDuration: '0.01ms !important',
                  animationIterationCount: '1 !important',
                  transitionDuration: '0.01ms !important',
                },
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                border: `1px solid ${isDark ? 'rgba(163,190,218,.12)' : 'rgba(29,65,101,.08)'}`,
                boxShadow: isDark
                  ? '0 18px 50px rgba(0, 0, 0, .20)'
                  : '0 12px 36px rgba(40, 75, 110, .08)',
                backgroundImage: 'none',
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: {
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 12,
                minHeight: 42,
                transition: 'transform 160ms ease, box-shadow 160ms ease, background-color 160ms ease',
                '&:active': { transform: 'translateY(1px)' },
              },
              containedPrimary: {
                background: 'linear-gradient(135deg, #1597E5 0%, #0876BA 100%)',
                boxShadow: '0 8px 20px rgba(21,151,229,.24)',
                '&:hover': { boxShadow: '0 10px 26px rgba(21,151,229,.34)' },
              },
            },
          },
          MuiChip: {
            styleOverrides: { root: { fontWeight: 700, borderRadius: 9 } },
          },
          MuiIconButton: {
            styleOverrides: {
              root: {
                borderRadius: 12,
                transition: 'background-color 160ms ease, transform 160ms ease',
                '&:active': { transform: 'scale(.96)' },
              },
            },
          },
          MuiTextField: {
            defaultProps: {
              variant: 'outlined',
              InputLabelProps: { shrink: true },
            },
          },
          MuiSelect: {
            defaultProps: { variant: 'outlined' },
          },
          MuiFormControl: {
            defaultProps: { variant: 'outlined' },
          },
          MuiOutlinedInput: {
            styleOverrides: {
              root: {
                borderRadius: 12,
                backgroundColor: isDark ? 'rgba(255,255,255,.025)' : 'rgba(248,251,254,.84)',
                transition: 'box-shadow 160ms ease, background-color 160ms ease',
                '&.Mui-focused': { boxShadow: '0 0 0 4px rgba(21,151,229,.10)' },
              },
              notchedOutline: {
                legend: { fontSize: '0.85em' },
              },
            },
          },
          MuiInputLabel: {
            styleOverrides: {
              outlined: {
                backgroundColor: isDark ? '#0D1A2B' : '#ffffff',
                paddingLeft: 4,
                paddingRight: 4,
              },
            },
          },
          MuiTooltip: {
            styleOverrides: { tooltip: { borderRadius: 9, fontWeight: 600 } },
          },
          MuiMenu: {
            styleOverrides: { paper: { borderRadius: 14, marginTop: 6 } },
          },
          MuiMenuItem: {
            styleOverrides: { root: { minHeight: 44, borderRadius: 9, margin: '2px 6px' } },
          },
          MuiTableRow: {
            styleOverrides: {
              root: { transition: 'background-color 140ms ease' },
            },
          },
          MuiTabs: {
            styleOverrides: { indicator: { height: 3, borderRadius: '3px 3px 0 0' } },
          },
          MuiTab: {
            styleOverrides: { root: { textTransform: 'none', fontWeight: 700, minHeight: 48 } },
          },
          MuiSnackbarContent: {
            styleOverrides: {
              root: { borderRadius: 14, boxShadow: '0 16px 45px rgba(7,17,31,.22)' },
            },
          },
          MuiAlert: {
            styleOverrides: { root: { borderRadius: 12 } },
          },
          MuiDialogContent: {
            styleOverrides: {
              root: {
                paddingTop: '20px !important',
              },
            },
          },
        },
      })
    },
    [mode, dir, language],
  )

  const cache = dir === 'rtl' ? rtlCache : ltrCache

  return (
    <ThemeContext.Provider value={{ mode, toggleMode, language, setLanguage }}>
      <CacheProvider value={cache}>
        <MuiThemeProvider theme={theme}>
          <CssBaseline />
          {children}
        </MuiThemeProvider>
      </CacheProvider>
    </ThemeContext.Provider>
  )
}
